// On-premises server inventory (docker ps of each server, Prometheus targets and Nginx Proxy Manager hosts).
// Last collected: 2026-11-03. Update this file when a container, port or proxy host changes.

import { MapIssue, MapLink, NodeKind, NodeState } from "../archmap/types";

export type ServerStatus = "migrated" | "kept" | "partial" | "pending" | "retired";

export interface Service {
  id: string;
  name: string;
  /** Host ports as published (host side) */
  port?: string;
  /** Container ports, only when different from the host port */
  containerPort?: string;
  image?: string;
  kind: NodeKind;
  group: string;
  state?: NodeState;
  note?: string;
}

export interface Server {
  id: string;
  dns: string;
  ip: string;
  user?: string;
  os: string;
  role: string;
  status: ServerStatus;
  /** Old VMs whose services moved to this server */
  replaces?: string[];
  note?: string;
  services: Service[];
}

export interface PublicHost {
  id: string;
  host: string;
  /** Service or server id */
  target: string;
  /** Forward target as configured in Nginx Proxy Manager */
  forward: string;
  warn?: string;
}

export interface RetiredServer {
  id: string;
  ip: string;
  dns: string;
  was: string;
  movedTo: string;
}

/** Systems outside the on-prem Docker servers that the services call */
export interface ExternalSystem {
  id: string;
  name: string;
  tag?: string;
  group: string;
  note?: string;
}

type Link = MapLink;

const exporters = (server: string, ports: string, note?: string): Service => ({
  id: `${server}-exp`,
  name: "exporters",
  port: ports,
  kind: "monitor",
  group: "Monitoring",
  note,
});

export const servers: Server[] = [
  {
    id: "s193",
    dns: "dev-rpx",
    ip: "192.168.66.193",
    user: "rpxr",
    os: "Ubuntu 24.04.1 LTS",
    role: "Reverse proxy – all *.genesiscloud.cc DNS entries point here",
    status: "kept",
    services: [
      { id: "npm", name: "nginx-proxy-manager", port: "80 · 443 · 81", image: "jc21/nginx-proxy-manager", kind: "proxy", group: "Proxy", note: "TLS certificates; admin UI on 81 (erprpx / reverse)" },
      { id: "erpbalancer", name: "erpbalancer", port: "8080", containerPort: "80", image: "docker-erpbalancer", kind: "proxy", group: "ERP" },
      { id: "erpbalancer-dev", name: "erpbalancer-dev", port: "8083", containerPort: "80", image: "docker-erpbalancer-dev", kind: "proxy", group: "ERP" },
      { id: "erpgateway-pr", name: "erpgateway-pr", port: "5004", containerPort: "5000", image: "docker-erpgateway-pr", kind: "app", group: "ERP" },
      { id: "erpgateway-re", name: "erpgateway-re", port: "5003", containerPort: "5000", image: "docker-erpgateway-re", kind: "app", group: "ERP" },
      exporters("s193", "9100 · 8081 · 9001"),
    ],
  },
  {
    id: "s3",
    dns: "dev-003",
    ip: "10.192.193.3",
    user: "dev",
    os: "Debian 13 (trixie)",
    role: "Internal API & ERP data gateways",
    status: "migrated",
    replaces: ["66.111", "66.28"],
    services: [
      { id: "internalapi-dev", name: "genesis-internal-api-dev", port: "44367", containerPort: "8080", kind: "app", group: "Internal API", note: "internalapi-re.genesiscloud.cc points here on purpose" },
      { id: "internalapi-pr", name: "genesis-internal-api-pr", port: "44369", containerPort: "8080", kind: "app", group: "Internal API", note: "No proxy host" },
      { id: "job-saphr", name: "SapHrSync", port: "Mon–Sat 05–21 · 60 min", kind: "job", group: "Internal API jobs (Scheduler)", state: "disabled", note: "Reads bookings and personal data from SAP HR (OData). Enabled: false in appsettings." },
      { id: "job-co2", name: "Co2Automatic", port: "once · 1440 min", kind: "job", group: "Internal API jobs (Scheduler)", state: "disabled", note: "CO2 calculation for companies 140, 310, 315 from Recy (purge, locations km, purchases, transport, account reports). Enabled: false in appsettings." },
      { id: "job-janitza", name: "JanitzaCollection", port: "daily 05:00", kind: "job", group: "Internal API jobs (Scheduler)", state: "disabled", note: "Collects equipment data points from Janitza, up to 4 equipments in parallel. Enabled: false in appsettings." },
      { id: "job-rentri", name: "RentriValidation", port: "Mon–Fri 16–18 · 30 min", kind: "job", group: "Internal API jobs (Scheduler)", state: "disabled", note: "Validates Rentri orders in batches of 20 (config partition rentri). Enabled: false in appsettings." },
      { id: "erpdg-prod", name: "erpdatagateway-prod", port: "4445", containerPort: "5000", kind: "app", group: "ERP data gateway" },
      { id: "erpdg-re", name: "erpdatagateway-re", port: "4446", containerPort: "5000", kind: "app", group: "ERP data gateway" },
      { id: "janitza", name: "equipments-janitza-api-pr", port: "5000", containerPort: "8080", kind: "app", group: "Equipment", note: "Reads the Janitza device 10.33.51.63:8080" },
      exporters("s3", "9100 · 8081 · 9001"),
    ],
  },
  {
    id: "s8",
    dns: "dev-008",
    ip: "10.192.193.8",
    user: "dev",
    os: "Debian 13 (trixie)",
    role: "Apps1 – E-Invoicing, AI front ends, MCP servers",
    status: "migrated",
    replaces: ["66.11"],
    services: [
      { id: "einv-web", name: "einv-dev-web", port: "8095", containerPort: "8080", image: "einv-dev/web", kind: "app", group: "E-Invoicing", note: "nginx + React SPA, forwards /api to portal-api" },
      { id: "einv-backend", name: "einv-dev · 7 services", port: "intern", image: "einv-dev/*", kind: "app", group: "E-Invoicing", note: "portal-api, inbound, parser, validation, triage, classification, dispatch (+ migrator, one-shot). No published ports." },
      { id: "semantic-api", name: "semantic-api-dev", port: "8089", containerPort: "8080", kind: "ai", group: "AI platform", note: "Only entry point for AI calls (agents, RAG, monitoring errors). Uses the database semantic-api-pr on postgresProd." },
      { id: "aicontrolhub", name: "aicontrolhub-dev", port: "5173", kind: "ai", group: "AI platform" },
      { id: "workspace", name: "operations-workspace-dev", port: "5176", containerPort: "5175", kind: "ai", group: "AI platform" },
      { id: "openwebui", name: "open-webui", port: "80", containerPort: "8080", image: "loacker-open-webui-dev:v0.10.1", kind: "ai", group: "Open WebUI", state: "warn", note: "Production instance runs the -dev image tag" },
      { id: "openwebui-dev", name: "open-webuiDev", port: "81", containerPort: "8080", image: "loacker-open-webui-dev:v0.10.1", kind: "ai", group: "Open WebUI" },
      { id: "mcp-translation", name: "translation-mcp", port: "8084", containerPort: "8080", kind: "ai", group: "MCP servers" },
      { id: "mcp-translation-dev", name: "translation-mcp-dev", port: "8083", containerPort: "8080", kind: "ai", group: "MCP servers" },
      { id: "mcp-outlook-dev", name: "outlook-mcp-dev", port: "8085", containerPort: "8080", kind: "ai", group: "MCP servers" },
      { id: "mcp-powerbi-dev", name: "powerbi-mcp-dev", port: "8087", containerPort: "8080", kind: "ai", group: "MCP servers" },
      { id: "n8n", name: "n8n", port: "5678", image: "n8nio/n8n", kind: "app", group: "Automation" },
      exporters("s8", "9100 · 8081 · 9001"),
    ],
  },
  {
    id: "s201",
    dns: "atgolosai01",
    ip: "10.192.193.201",
    user: "gpuadmin",
    os: "Ubuntu 26.04.1 LTS",
    role: "KI server – GPU models (compose project onprem-ai)",
    status: "migrated",
    replaces: ["66.31"],
    note: "Ports bound to 10.192.193.201 only",
    services: [
      { id: "qwen-smart", name: "qwen-smart", port: "8001", containerPort: "8000", image: "vLLM (untagged a8d84c552462)", kind: "ai", group: "LLM (vLLM)", state: "warn", note: "Image without tag – pin a vLLM version" },
      { id: "qwen-standard", name: "qwen-standard", port: "8002", containerPort: "8000", image: "vllm/vllm-openai:v0.27.1", kind: "ai", group: "LLM (vLLM)" },
      { id: "dev-coder", name: "dev-coder", port: "8005", containerPort: "8000", image: "vllm/vllm-openai:v0.30.0", kind: "ai", group: "LLM (vLLM)" },
      { id: "ministral-fast", name: "ministral-fast", image: "vllm/vllm-openai:v0.27.1", kind: "ai", group: "LLM (vLLM)", state: "stopped", note: "Stopped for 2 weeks – remove or restart" },
      { id: "embedding", name: "embedding", port: "8082", containerPort: "80", image: "text-embeddings-inference:cuda-1.9", kind: "ai", group: "RAG" },
      { id: "reranker", name: "reranker", port: "8083", containerPort: "80", image: "text-embeddings-inference:cuda-1.9", kind: "ai", group: "RAG" },
      { id: "docling", name: "docling-serve", port: "5001", image: "docling-serve-cu128:v1.31.0", kind: "ai", group: "RAG" },
      exporters("s201", "9100 · 8081 · 9400", "node_exporter 9100, cadvisor 8081, dcgm-exporter (GPU) 9400; no portainer-agent (not needed)"),
    ],
  },
  {
    id: "s7",
    dns: "dev-007",
    ip: "10.192.193.7",
    user: "dev",
    os: "Debian 13 (trixie)",
    role: "DB1 – databases, vector stores, object storage, broker",
    status: "migrated",
    replaces: ["66.21"],
    services: [
      { id: "pg-dev", name: "postgresDev", port: "5438", kind: "db", group: "PostgreSQL 17" },
      { id: "pg-prod", name: "postgresProd", port: "5439", kind: "db", group: "PostgreSQL 17" },
      { id: "pg-owui", name: "postgresOpenWebUI", port: "5431", kind: "db", group: "PostgreSQL 17" },
      { id: "pg-owui-dev", name: "postgresOpenWebUIDev", port: "5432", kind: "db", group: "PostgreSQL 17" },
      { id: "pg-litellm", name: "postgreslitell", port: "5433", kind: "db", group: "PostgreSQL 17" },
      { id: "pg-langfuse", name: "postgreslangf", port: "5435", image: "postgres 17.8", kind: "db", group: "PostgreSQL 17", note: "17.8 – the other instances run 17.5" },
      { id: "pg-n8n", name: "postgresn8n", port: "5437", kind: "db", group: "PostgreSQL 17" },
      { id: "redis-prod", name: "redisProd", port: "6379", kind: "db", group: "Redis" },
      { id: "redis-dev", name: "redisDev", port: "6378", containerPort: "6379", kind: "db", group: "Redis" },
      { id: "redisinsight", name: "redisIn (RedisInsight)", port: "5540", kind: "monitor", group: "Redis" },
      { id: "qdrant", name: "qdrant", port: "6333 · 6334", kind: "db", group: "Vector & search" },
      { id: "qdrant-dev", name: "qdrantDev", port: "6331 · 6332", containerPort: "6333 · 6334", kind: "db", group: "Vector & search" },
      { id: "opensearch", name: "rag-opensearch", port: "9200 · 9600", image: "opensearch:2.19.0", kind: "db", group: "Vector & search" },
      { id: "os-dashboards", name: "rag-dashboards", port: "5601", image: "opensearch-dashboards:2.19.0", kind: "monitor", group: "Vector & search" },
      { id: "rustfs", name: "rustfsDev (S3)", port: "9007 · 9008", containerPort: "9000 · 9001", kind: "db", group: "Object storage", note: "9007 S3 API, 9008 console" },
      { id: "rabbitmq-dev", name: "rabbitmqDev", port: "5673 · 15673", containerPort: "5672 · 15672", image: "rabbitmq:4-management", kind: "queue", group: "Messaging", note: "vhost /einv (E-Invoicing). Queue metrics are not in Prometheus (would need port 15692)." },
      exporters("s7", "9100 · 8081 · 9001"),
    ],
  },
  {
    id: "s6",
    dns: "dev-006",
    ip: "10.192.193.6",
    user: "dev",
    os: "Debian 13 (trixie)",
    role: "Monitoring, LLM gateway, vault",
    status: "migrated",
    replaces: ["66.206 (monitoring, vault)"],
    services: [
      { id: "litellm", name: "litellm", port: "4000", image: "ghcr.io/berriai/litellm:main-stable", kind: "ai", group: "LLM gateway", state: "warn", note: "Healthcheck unhealthy, requests work" },
      { id: "prometheus", name: "prometheus", port: "9090", kind: "monitor", group: "Monitoring" },
      { id: "grafana", name: "grafana", port: "3000", kind: "monitor", group: "Monitoring" },
      { id: "blackbox", name: "blackbox-exporter", port: "9115", kind: "monitor", group: "Monitoring", note: "Job api-health-check" },
      { id: "portainer", name: "portainer (server)", port: "9443 · 8000", kind: "monitor", group: "Monitoring" },
      { id: "bitwarden", name: "bitwarden (Vaultwarden)", port: "8080 · 3012", image: "bitwardenrs/server – 1.24.0", kind: "security", group: "Vault", state: "warn", note: "Migrated from .206 on 2026-11-02. Upgrade to vaultwarden/server (pinned)." },
      { id: "nginx-6", name: "nginx", kind: "proxy", group: "Leftover", state: "down", note: "Restart loop (upstream bitwarden) – remove" },
      exporters("s6", "9100 · 8081"),
    ],
  },
  {
    id: "s206",
    dns: "dev-mon",
    ip: "192.168.66.206",
    os: "Linux",
    role: "Old monitoring VM – only the wiki stays (not ours)",
    status: "partial",
    services: [
      { id: "wiki", name: "wikijs + nginx", port: "443", kind: "app", group: "Wiki", note: "nginx serves only wiki.genesiscloud.cc" },
    ],
  },
  {
    id: "s1",
    dns: "atgolosdev1",
    ip: "192.168.66.1",
    os: "Windows Server",
    role: "ERP gateways – migration to 10.192.193.1 planned",
    status: "pending",
    services: [
      { id: "s1-rona", name: "rona gateway", port: "3443 · 3442", kind: "app", group: "PROD · RE" },
      { id: "s1-recy", name: "recy gateway", port: "4443 · 4442", kind: "app", group: "PROD · RE" },
      { id: "s1-telltex", name: "telltex gateway", port: "2443 · 2442", kind: "app", group: "PROD · RE" },
      { id: "s1-exp", name: "windows_exporter", port: "9182", kind: "monitor", group: "Monitoring" },
    ],
  },
  {
    id: "s2",
    dns: "atgolosdev2",
    ip: "192.168.66.2",
    os: "Windows Server",
    role: "ERP gateways – migration to 10.192.193.2 planned",
    status: "pending",
    services: [
      { id: "s2-rona", name: "rona gateway", port: "3443 · 3442", kind: "app", group: "PROD · RE" },
      { id: "s2-recy", name: "recy gateway", port: "4443 · 4442", kind: "app", group: "PROD · RE" },
      { id: "s2-telltex", name: "telltex gateway", port: "2443 · 2442", kind: "app", group: "PROD · RE" },
      { id: "s2-exp", name: "windows_exporter", port: "9182", kind: "monitor", group: "Monitoring" },
    ],
  },
];

export const retired: RetiredServer[] = [
  { id: "r11", ip: "192.168.66.11", dns: "dev-ai", was: "Open WebUI, MCP servers", movedTo: "s8" },
  { id: "r21", ip: "192.168.66.21", dns: "dev-aicont", was: "Open WebUI DBs, Qdrant, Redis", movedTo: "s7" },
  { id: "r31", ip: "192.168.66.31", dns: "dev-emb", was: "DEV AI – ollama, docling", movedTo: "s201" },
  { id: "r111", ip: "192.168.66.111", dns: "atgolosdevdock1", was: "Internal API (WSL)", movedTo: "s3" },
  { id: "r28", ip: "192.168.66.28", dns: "angio4detallesapi", was: "erpdatagateway, Internal API", movedTo: "s3" },
];

const gc = (name: string) => `${name}.genesiscloud.cc`;

export const publicHosts: PublicHost[] = [
  { id: "h-einvoice-dev", host: gc("einvoice-dev"), target: "einv-web", forward: "10.192.193.8:8095" },
  { id: "h-ai", host: gc("ai"), target: "workspace", forward: "10.192.193.8:5176" },
  { id: "h-workspace", host: gc("workspace"), target: "workspace", forward: "10.192.193.8:5176" },
  { id: "h-aicontrolhub", host: gc("aicontrolhub"), target: "aicontrolhub", forward: "10.192.193.8:5173" },
  { id: "h-n8n", host: gc("n8n"), target: "n8n", forward: "10.192.193.8:5678" },
  { id: "h-internalapi-re", host: gc("internalapi-re"), target: "internalapi-dev", forward: "10.192.193.3:44367" },
  { id: "h-litellm", host: gc("litellm"), target: "litellm", forward: "10.192.193.6:4000" },
  { id: "h-vault", host: gc("vault"), target: "bitwarden", forward: "10.192.193.6:8080 (+ /notifications/hub → 3012)" },
  { id: "h-dev-portainer", host: gc("dev.portainer"), target: "portainer", forward: "https://10.192.193.6:9443" },
  { id: "h-objectdb", host: gc("objectdb"), target: "rustfs", forward: "10.192.193.7:9008" },
  { id: "h-queue-dev", host: gc("queue-dev"), target: "rabbitmq-dev", forward: "10.192.193.7:15673" },
  { id: "h-redisin", host: gc("redisin"), target: "redisinsight", forward: "10.192.193.7:5540" },
  { id: "h-vector", host: gc("vector"), target: "os-dashboards", forward: "10.192.193.7:5601" },
  { id: "h-qdrant", host: gc("qdrant"), target: "qdrant", forward: "10.192.193.7:6333" },
  { id: "h-qdrant-local", host: gc("qdrant-local"), target: "qdrant", forward: "10.192.193.7:6333" },
  { id: "h-qdrant-dev", host: gc("qdrant-dev"), target: "qdrant-dev", forward: "10.192.193.7:6331" },
  { id: "h-flow", host: gc("flow"), target: "erpbalancer", forward: "https://erpbalancer:80" },
  { id: "h-gpt", host: "gpt.loacker.cc", target: "erpbalancer", forward: "https://erpbalancer:80" },
  { id: "h-gpt-dev", host: "gpt-dev.loacker.cc", target: "erpbalancer-dev", forward: "https://erpbalancer-dev:80" },
  { id: "h-erpgwpr", host: gc("erpgwpr"), target: "erpgateway-pr", forward: "erpgateway-pr:5000" },
  { id: "h-erpgwre", host: gc("erpgwre"), target: "erpgateway-re", forward: "erpgateway-re:5000" },
  { id: "h-erprpx", host: gc("erprpx"), target: "npm", forward: "127.0.0.1:81" },
  { id: "h-reverse", host: gc("reverse"), target: "npm", forward: "127.0.0.1:81" },
  { id: "h-dev1", host: gc("dev1"), target: "s1", forward: "192.168.66.1:4444" },
  { id: "h-dev2", host: gc("dev2"), target: "s2", forward: "192.168.66.2:3000" },
  { id: "h-wiki", host: gc("wiki"), target: "wiki", forward: "nginx on 192.168.66.206 (not a Nginx Proxy Manager host)" },
  { id: "h-monitor", host: gc("monitor"), target: "grafana", forward: "10.192.193.6:3000" },
  { id: "h-portainer", host: gc("portainer"), target: "portainer", forward: "https://10.192.193.6:9443" },
];

export const externals: ExternalSystem[] = [
  { id: "ext-sql-recy", name: "SQL Server RECY / RECYTEST", tag: "atgolossql01", group: "ERP databases" },
  { id: "ext-sql-rona", name: "SQL Server Rona (loacker_prod)", tag: "atgolos214", group: "ERP databases" },
  { id: "ext-recy-online", name: "Recy Online · BusinessProcesses", tag: "66.26:4452", group: "ERP databases", note: "Checked by the blackbox job api-health-check" },
  { id: "ext-azure-storage", name: "Azure Storage", group: "Cloud", note: "Master data, material data, accounts, production, users, communications" },
  { id: "ext-graph", name: "Microsoft Graph", group: "Cloud", note: "Files, Teams, calendar (semantic-api); app token (Internal API)" },
  { id: "ext-google", name: "Google Custom Search", group: "Cloud", note: "Internet search of semantic-api" },
  { id: "ext-sap-hr", name: "SAP HR (OData)", group: "Other systems" },
  { id: "ext-janitza", name: "Janitza device", tag: "10.33.51.63:8080", group: "Other systems" },
];

const monitored = ["s193-exp", "s3-exp", "s8-exp", "s201-exp", "s7-exp", "s6-exp", "s1-exp", "s2-exp"];

export const links: Link[] = [
  ...publicHosts.map((h): Link => ({ from: h.id, to: h.target, kind: "proxy", label: h.forward })),

  // E-Invoicing
  { from: "einv-web", to: "einv-backend", kind: "api", label: "/api → portal-api" },
  { from: "einv-backend", to: "pg-dev", kind: "data", label: "DB einvoicing" },
  { from: "einv-backend", to: "redis-dev", kind: "data", label: "monitoring" },
  { from: "einv-backend", to: "rustfs", kind: "data", label: "bucket einv-originals" },
  { from: "einv-backend", to: "rabbitmq-dev", kind: "data", label: "vhost /einv" },
  { from: "einv-backend", to: "semantic-api", kind: "ai", label: "/v2/agents/completions" },
  { from: "einv-backend", to: "internalapi-dev", kind: "api", label: "user roles" },

  // Internal API (appsettings of genesis-internal-api-dev)
  { from: "internalapi-dev", to: "pg-prod", kind: "data", label: "DB sales" },
  { from: "internalapi-dev", to: "erpgateway-re", kind: "api", label: "erpgwre GraphQL" },
  { from: "internalapi-dev", to: "ext-sql-recy", kind: "data", label: "RECY" },
  { from: "internalapi-dev", to: "ext-sql-rona", kind: "data", label: "loacker_prod" },
  { from: "internalapi-dev", to: "ext-azure-storage", kind: "data", label: "master / accounts / users" },
  { from: "internalapi-dev", to: "ext-graph", kind: "api" },
  { from: "internalapi-dev", to: "job-saphr", kind: "api", label: "scheduler" },
  { from: "internalapi-dev", to: "job-co2", kind: "api", label: "scheduler" },
  { from: "internalapi-dev", to: "job-janitza", kind: "api", label: "scheduler" },
  { from: "internalapi-dev", to: "job-rentri", kind: "api", label: "scheduler" },
  { from: "job-saphr", to: "ext-sap-hr", kind: "api", label: "OData" },
  { from: "job-co2", to: "ext-sql-recy", kind: "data", label: "Recy Prod" },
  { from: "job-janitza", to: "janitza", kind: "api", assumed: true },
  { from: "janitza", to: "ext-janitza", kind: "api" },

  // AI platform (appsettings of semantic-api-dev)
  { from: "semantic-api", to: "litellm", kind: "ai", label: "models smart / fast / standard / embedding-h200" },
  { from: "semantic-api", to: "pg-prod", kind: "data", label: "DB semantic-api-pr" },
  { from: "semantic-api", to: "redis-dev", kind: "data", label: "policy cache" },
  { from: "semantic-api", to: "opensearch", kind: "data", label: "document_chunks" },
  { from: "semantic-api", to: "rustfs", kind: "data", label: "buckets semantic-api-*" },
  { from: "semantic-api", to: "docling", kind: "ai", label: "/v1/convert/file" },
  { from: "semantic-api", to: "mcp-outlook-dev", kind: "ai", label: "MCP" },
  { from: "semantic-api", to: "mcp-translation-dev", kind: "ai", label: "MCP" },
  { from: "semantic-api", to: "mcp-powerbi-dev", kind: "ai", label: "MCP" },
  { from: "semantic-api", to: "ext-graph", kind: "api" },
  { from: "semantic-api", to: "ext-google", kind: "api" },
  { from: "aicontrolhub", to: "semantic-api", kind: "api", assumed: true },
  { from: "aicontrolhub", to: "pg-prod", kind: "data", assumed: true },
  { from: "workspace", to: "semantic-api", kind: "api", assumed: true },
  { from: "litellm", to: "pg-litellm", kind: "data" },
  { from: "litellm", to: "qwen-smart", kind: "ai" },
  { from: "litellm", to: "qwen-standard", kind: "ai" },
  { from: "litellm", to: "dev-coder", kind: "ai" },
  { from: "litellm", to: "embedding", kind: "ai", label: "embedding-h200" },
  { from: "litellm", to: "reranker", kind: "ai", label: "reranker (off in semantic-api)", assumed: true },

  // Open WebUI and n8n
  { from: "openwebui", to: "pg-owui", kind: "data" },
  { from: "openwebui", to: "qdrant", kind: "data", label: "RAG" },
  { from: "openwebui", to: "redis-prod", kind: "data", assumed: true },
  { from: "openwebui", to: "litellm", kind: "ai" },
  { from: "openwebui", to: "docling", kind: "ai", label: "document parsing", assumed: true },
  { from: "openwebui-dev", to: "pg-owui-dev", kind: "data" },
  { from: "openwebui-dev", to: "qdrant-dev", kind: "data", label: "RAG", assumed: true },
  { from: "openwebui-dev", to: "litellm", kind: "ai" },
  { from: "n8n", to: "pg-n8n", kind: "data" },

  // Monitoring
  { from: "grafana", to: "prometheus", kind: "monitor" },
  { from: "prometheus", to: "blackbox", kind: "monitor" },
  { from: "blackbox", to: "ext-recy-online", kind: "monitor", label: "http probe" },
  { from: "blackbox", to: "h-erpgwpr", kind: "monitor", label: "GraphQL probe" },
  { from: "blackbox", to: "h-erpgwre", kind: "monitor", label: "GraphQL probe" },
  ...monitored.map((id): Link => ({ from: "prometheus", to: id, kind: "monitor", label: "scrape" })),
  ...monitored
    .filter((id) => !["s201-exp", "s1-exp", "s2-exp"].includes(id))
    .map((id): Link => ({ from: "portainer", to: id, kind: "monitor", label: "agent 9001" })),
];

export const openIssues: MapIssue[] = [
  { text: "litellm healthcheck reports unhealthy", target: "litellm" },
  { text: "nginx on dev-006 in restart loop – remove container and compose entry", target: "nginx-6" },
  { text: "Vaultwarden 1.24.0 (bitwardenrs) – upgrade to vaultwarden/server, pinned", target: "bitwarden" },
  { text: "open-webui (prod) uses the -dev image tag", target: "openwebui" },
  { text: "qwen-smart image has no tag", target: "qwen-smart" },
  { text: "ministral-fast stopped for 2 weeks", target: "ministral-fast" },
  { text: "semantic-api-dev writes to the database semantic-api-pr on postgresProd", target: "semantic-api" },
];
